import { useQuery } from '@tanstack/react-query';
import { FilterMatchMode, FilterOperator, FilterService } from 'primereact/api';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { DataTablePageEvent } from 'primereact/datatable';
import { TENDER_LIST } from '../constants/queryKeys';
import useSessionStorage from './useSessionStorage';
import useAxiosPrivate from './useAxiosPrivate';
import { TenderListFrame } from '../api/_generated_';
import { Sort } from '../components/types/types';

const MULTISELECT_MATCHER = 'multi-select-matcher';

const INITIAL_FILTERS = {
  global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  offerGroup: { value: '', matchMode: FilterMatchMode.EQUALS },
  productGroup: { value: '', matchMode: MULTISELECT_MATCHER },
  subProductGroup: { value: '', matchMode: FilterMatchMode.EQUALS },
  countries: { value: '', matchMode: MULTISELECT_MATCHER },
  offeringEndDate: {
    operator: FilterOperator.AND,
    constraints: [
      { value: null, matchMode: FilterMatchMode.DATE_AFTER },
      { value: null, matchMode: FilterMatchMode.DATE_BEFORE },
    ],
  },
};

FilterService.register(MULTISELECT_MATCHER, (value, filter) => {
  if (filter.length === 0) {
    return true;
  }
  const values = value ? value.split(',').map((v: string) => v.trim()) : [];
  return values.filter((v: string) => filter.includes(v)).length > 0;
});

export default function useTenders() {
  const axios = useAxiosPrivate();
  const { i18n } = useTranslation();

  const [filters, setFilters] = useSessionStorage('alko-tender-filters', INITIAL_FILTERS);
  const [sort, setSort] = useSessionStorage('alko-tender-sort', null);

  // Convert date strings back to Date objects after loading from localStorage
  const normalizedFilters = {
    ...filters,
    offeringEndDate: {
      ...filters.offeringEndDate,
      constraints: filters.offeringEndDate.constraints.map((constraint: any) => ({
        ...constraint,
        value: constraint.value && typeof constraint.value === 'string' ? new Date(constraint.value) : constraint.value,
      })),
    },
  };

  async function getTenders(): Promise<TenderListFrame> {
    const res = await axios.get<TenderListFrame>(`/tender/list-frame?locale=${i18n.language}`);
    const { data } = res;

    data.tenders = data.tenders?.map((item: any) => ({
      ...item,
      offeringEndDate: new Date(item.offeringEndDate),
    }));

    return data;
  }

  const { data, status } = useQuery([TENDER_LIST], getTenders);

  const onChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setFilters({
      ...normalizedFilters,
      [name]: { ...normalizedFilters[name], value },
    });
  };

  const handleClearFilter = () => {
    setFilters(JSON.parse(JSON.stringify(INITIAL_FILTERS)));
  };

  const onChangeDeadlineFilter = (e: any) => {
    const { value, name } = e.target;
    const index = name === 'deadlineAtleast' ? 0 : 1;
    const newConstraints = normalizedFilters.offeringEndDate.constraints.map(
      (constraint: any, i: number) => (i === index ? { ...constraint, value } : constraint),
    );

    setFilters({
      ...normalizedFilters,
      offeringEndDate: {
        ...normalizedFilters.offeringEndDate,
        constraints: newConstraints,
      },
    });
  };

  const onSort = useCallback((e: DataTablePageEvent) => {
    setSort({
      field: e.sortField,
      order: e.sortOrder,
    });
  }, []);

  return {
    status,
    data,
    filters: normalizedFilters,
    deadlineStart: normalizedFilters.offeringEndDate.constraints[0].value,
    deadlineEnd: normalizedFilters.offeringEndDate.constraints[1].value,
    onChangeDeadlineFilter,
    onChangeFilter,
    handleClearFilter,
    sort: sort as Sort | null,
    onSort,
  };
}
